import type { DateRange } from "@/domains/v4b/site-consumption/site-consumption.types";

import { MaybeRefOrGetter, Ref } from "vue";

import {
  TMultisiteConsumptionRequest,
  TSiteIdsQuery,
} from "@/data/api/kili/consumption/types/multisite-consumption.type";
import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { SiteSectionCategoryId, SiteSectionId } from "@/data/common/ids";

export enum EConsumptionPerimeterType {
  MONOSITE = "monosite",
  MULTISITE = "multisite",
}

export enum EConsumptionQueryAction {
  FIND_DEVICES_CONSUMPTION = "find-devices-consumption",
  FIND_DSO_CONSUMPTION = "find-dso-consumption",
  FIND_CONSUMPTION_INFOS = "find-consumption-informations",
  FIND_MISSING_INFOS = "find-consumption-missing-infos",
  GET_HEATMAP_CONSUMPTION = "get-heatmap-consumption",
  GET_SITES_PERIMETER = "get-sites-perimeter",
}

const query = {
  api: "kili",
  type: "query",
  scope: "consumption",
} as const;

const siteQuery = (siteId: MaybeRefOrGetter<number>) =>
  [...SiteQueryKeys.site(siteId), query, { perimeter: EConsumptionPerimeterType.MONOSITE }] as const;

export const ConsumptionQueryKeys = {
  all: [query] as const,
  site: siteQuery,
  siteConsumption: (
    siteId: Ref<number>,
    dateRange: Ref<DateRange>,
    requestedSectionIds: MaybeRefOrGetter<Array<SiteSectionId> | undefined>,
    requestedSectionCategoryIds: MaybeRefOrGetter<Array<SiteSectionCategoryId> | undefined>,
  ) =>
    [
      ...siteQuery(siteId),
      { perimeter: EConsumptionPerimeterType.MONOSITE, dateRange, requestedSectionIds, requestedSectionCategoryIds },
    ] as const,
  siteConsumptionInformations: (siteId: Ref<number>) =>
    [
      ...siteQuery(siteId),
      {
        perimeter: EConsumptionPerimeterType.MONOSITE,
        action: EConsumptionQueryAction.FIND_CONSUMPTION_INFOS,
      },
    ] as const,
  siteConsumptionMissingInfos: (siteId: Ref<number>) =>
    [
      ...siteQuery(siteId),
      {
        perimeter: EConsumptionPerimeterType.MONOSITE,
        action: EConsumptionQueryAction.FIND_MISSING_INFOS,
      },
    ] as const,
  multisite: () => [{ ...ConsumptionQueryKeys.all[0], perimeter: EConsumptionPerimeterType.MULTISITE }] as const,
  multisiteConsumption: (request: Ref<TMultisiteConsumptionRequest>) =>
    [{ ...ConsumptionQueryKeys.multisite()[0], params: { request } }] as const,
  multisiteConsumptionMissingInfos: (siteIds: Ref<TMultisiteConsumptionRequest["siteIds"]>) =>
    [
      {
        ...ConsumptionQueryKeys.multisite()[0],
        action: EConsumptionQueryAction.FIND_MISSING_INFOS,
        params: { siteIds },
      },
    ] as const,
  sitesPerimeter: () =>
    [{ ...ConsumptionQueryKeys.all[0], action: EConsumptionQueryAction.GET_SITES_PERIMETER }] as const,
  heatmapConsumption: (siteIds: Ref<TSiteIdsQuery>) =>
    [
      { ...ConsumptionQueryKeys.all[0], params: { siteIds }, action: EConsumptionQueryAction.GET_HEATMAP_CONSUMPTION },
    ] as const,
};

export const ConsumptionMutationKeys = {
  all: [{ api: "kili", type: "mutation", scope: "consumption" }] as const,
  editSiteConsumptionInformations: () =>
    [{ ...ConsumptionMutationKeys.all[0], action: "edit-site-consumption-infos" }] as const,
};
